import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { accentVarsFor } from '../components/AccentInjector';
import { PageSpinner } from '../components/Spinner';
import { OrgPublicPageView } from '../components/OrgPublicPageView';

// ──────────────────────────────────────────────────────────────────────────────
// OrgHome — members-only org profile page.
// Route: /org/:slug
//
// Backend: GET /portal/public/org/:slug — requires a valid JWT; guild membership
// is verified server-side. 401 = unauthenticated, 403 = not a member of the
// org's Discord server, 404 = page missing/unpublished, 200 = member + published.
//
// The actual rendered page (hero, stats, body sections, footer) lives in
// OrgPublicPageView — a pure presentational component shared with the org page
// editor's live Preview mode. This file owns only: data fetching, the
// whole-app accent side effect, and the loading/error guards.
// ──────────────────────────────────────────────────────────────────────────────

export function OrgHome() {
  // ALL hooks must be declared before any early return.
  const { slug } = useParams<{ slug: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: ['publicOrgPage', slug],
    queryFn: () => api.publicOrgPage(slug!),
    enabled: !!slug,
    retry: false,
  });

  // Apply org accent palette to <html> while this page is mounted.
  // accentVarsFor accepts accentOverride as a hex string (#rrggbb).
  const accentColor = data?.org.accentColor ?? null;
  useEffect(() => {
    if (!accentColor) return;
    const vars = accentVarsFor(null, accentColor);
    if (Object.keys(vars).length === 0) return;
    const root = document.documentElement;
    for (const [k, v] of Object.entries(vars)) {
      root.style.setProperty(k, v);
    }
    return () => {
      for (const k of Object.keys(vars)) {
        root.style.removeProperty(k);
      }
    };
  }, [accentColor]);

  // ── Early returns (all hooks above this line) ─────────────────────────────

  if (!slug) return null;

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <PageSpinner />
      </div>
    );
  }

  if (error || !data) {
    const msg = (error as Error | null)?.message ?? '';
    if (msg === 'unauthorized') {
      return (
        <div className="flex min-h-screen items-center justify-center p-6">
          <div className="max-w-sm text-center">
            <h2 className="mb-2 text-lg font-semibold text-white">Sign in to view this page</h2>
            <p className="text-sm text-gray-400">
              This page is only visible to members of the organisation's Discord server. Open the GG
              Anvil portal and sign in (run <code className="font-mono">/ggportal</code> in your Discord
              server to get a link), then reload.
            </p>
          </div>
        </div>
      );
    }
    if (msg === 'forbidden') {
      return (
        <div className="flex min-h-screen items-center justify-center p-6">
          <div className="max-w-sm text-center">
            <h2 className="mb-2 text-lg font-semibold text-white">Members only</h2>
            <p className="text-sm text-gray-400">
              You need to be a member of this organisation's Discord server to view this page.
              If you belong to this org, ask an admin to add you to its Discord.
            </p>
          </div>
        </div>
      );
    }
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <p className="max-w-sm text-center text-gray-400">
          {msg.includes('not_found')
            ? "This org hasn't published a public page yet."
            : 'Something went wrong loading this page.'}
        </p>
      </div>
    );
  }

  return <OrgPublicPageView data={data} />;
}
